import videojs from "video.js";

const Plugin = videojs.getPlugin('plugin');
const Component = videojs.getComponent('component');

class DisplayCalendarTime extends Plugin {
  /**
   * @param {Player} player
   * @param {Object} options
   */
  constructor(player, options) {
    super(player, options);
    this.setListeners();
  }

  setListeners() {
    this.player.on('durationchange', () => {
      this.patchComponents();
    });

    this.player.on('timeupdate', () => {
      this.patchComponents();
    });
  }

  patchComponents() {
    const isLive = this.isLivePlaylist();
    if (!isLive) return;

    this.player.addClass('vjs-calendar-time');

    const durationDisplay = this.player.getDescendant(['controlBar', 'durationDisplay']);
    if (durationDisplay && this.player.duration() !== Infinity) {
      durationDisplay.updateContent = () => this.updateTime(durationDisplay, this.player.duration());
    }

    const currentTimeDisplay = this.player.getDescendant(['controlBar', 'currentTimeDisplay']);
    if (currentTimeDisplay) {
      currentTimeDisplay.updateContent = () => this.updateTime(currentTimeDisplay, this.player.currentTime());
    }

    const playProgressBarTooltip = this.player.getDescendant(['controlBar', 'progressControl', 'seekBar', 'playProgressBar', 'timeTooltip']);
    if (playProgressBarTooltip) {
      playProgressBarTooltip.updateTime = (seekBarRect, seekBarPoint, time, cb) =>
        this.updateTooltipTime(seekBarRect, seekBarPoint, time, cb, playProgressBarTooltip);
    }

    const mouseTimeDisplayTooltip = this.player.getDescendant(['controlBar', 'progressControl', 'seekBar', 'mouseTimeDisplay', 'timeTooltip']);
    if (mouseTimeDisplayTooltip) {
      mouseTimeDisplayTooltip.updateTime = (seekBarRect, seekBarPoint, time, cb) =>
        this.updateTooltipTime(seekBarRect, seekBarPoint, time, cb, mouseTimeDisplayTooltip);
    }
  }

  /**
   * @param {Component} component
   * @param {number} time
   */
  updateTime(component, time) {
    const calendarTime = this.getCalendarTime(time);
    if (!calendarTime) return;

    component.el().textContent = calendarTime;
  }

  /**
   * @param {DOMRect} seekBarRect
   * @param {number} seekBarPoint
   * @param {number} time
   * @param {Function} cb
   * @param {Component} component
   */
  updateTooltipTime(seekBarRect, seekBarPoint, time, cb, component) {
    const requestNamedAnimationFrame = component.requestNamedAnimationFrame.bind(component);
    const update = component.update.bind(component);

    requestNamedAnimationFrame('TimeTooltip#updateTime', () => {
      const calendarTime = this.getCalendarTime(time);
      if (!calendarTime) return;

      update(seekBarRect, seekBarPoint, calendarTime);
      if (cb) {
        cb();
      }
    });
  }

  /**
   * @param {number} fragmentTime
   * @returns {string | null}
   */
  getCalendarTime(fragmentTime) {
    const fragment = this.findFragmentByTime(fragmentTime);
    if (!fragment) {
      return null;
    }

    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const calendarTime = new Date(fragment.programDateTime + (fragmentTime - fragment.start) * 1000);
    return calendarTime.toLocaleTimeString('en-GB', { timeZone: userTimeZone });
  }

  /**
   * @param {number} time
   * @returns {Object | null}
   */
  findFragmentByTime(time) {
    const hls = this.player.hls_;
    if (!hls.levels || !hls.levels[hls.currentLevel]) {
      return null;
    };

    const fragments = hls.levels[hls.currentLevel].details.fragments;
    if (!fragments || !fragments.length) {
      return null;
    };

    return fragments.find((fragment) => {
      const start = fragment.start;
      const end = fragment.start + fragment.duration;
      return time >= start && time <= end;
    });
  }

  /**
   * @returns {boolean}
   */
  isLivePlaylist() {
    const hls = this.player.hls_;
    const hlsCurrentLevel = hls.currentLevel === -1 ? 0 : hls.currentLevel;
    if (!hls.levels) {
      return false;
    };

    const hlsLevel = hls.levels[hlsCurrentLevel];
    return hlsLevel && hlsLevel.details && hlsLevel.details.live;
  }
}

videojs.registerPlugin('displayCalendarTime', DisplayCalendarTime);